import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getTaxonomies } from '../services/api'
import { useAuth } from './AuthContext'
import { DEFAULT_TAXONOMY, TAXONOMIES } from '../constants/taxonomies'

interface TaxonomyInfo {
  id: string
  name: string
  node_count: number
}

interface TaxonomyContextType {
  taxonomies: TaxonomyInfo[]
  activeTaxonomy: string
  setActiveTaxonomy: (id: string) => void
  loading: boolean
}

const TaxonomyContext = createContext<TaxonomyContextType | null>(null)

export function TaxonomyProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [taxonomies, setTaxonomies] = useState<TaxonomyInfo[]>(
    TAXONOMIES.map((t) => ({ id: t.id, name: t.name, node_count: t.nodeCount }))
  )
  const [activeTaxonomy, setActiveTaxonomyState] = useState<string>(
    user?.preferredTaxonomy || DEFAULT_TAXONOMY
  )
  const [loading, setLoading] = useState(false)

  // Fetch available taxonomies from API when authenticated
  useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false

    async function fetchTaxonomies() {
      setLoading(true)
      try {
        const data = await getTaxonomies()
        if (!cancelled && data && data.length > 0) {
          setTaxonomies(data)
        }
      } catch {
        // Fall back to static taxonomy list on error
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchTaxonomies()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  // Update active taxonomy when user's preference changes
  useEffect(() => {
    if (user?.preferredTaxonomy) {
      setActiveTaxonomyState(user.preferredTaxonomy)
    }
  }, [user?.preferredTaxonomy])

  const setActiveTaxonomy = useCallback((id: string) => {
    setActiveTaxonomyState(id)
  }, [])

  const value: TaxonomyContextType = {
    taxonomies,
    activeTaxonomy,
    setActiveTaxonomy,
    loading,
  }

  return <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>
}

export function useTaxonomy(): TaxonomyContextType {
  const context = useContext(TaxonomyContext)
  if (!context) {
    throw new Error('useTaxonomy must be used within a TaxonomyProvider')
  }
  return context
}
